import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string) => ReactNode;
}

function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-secondary">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-xs text-danger-text">{error}</p>
      ) : hint ? (
        <p className="text-xs text-tertiary">{hint}</p>
      ) : null}
    </div>
  );
}

const controlClasses = [
  'w-full rounded-md bg-surface px-3',
  'min-h-(--size-control-md)',
  'border border-(--color-border-default)',
  'text-primary placeholder:text-placeholder',
  'disabled:bg-disabled disabled:text-disabled-text',
].join(' ');

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {(id) => (
        <input
          {...rest}
          id={id}
          aria-invalid={error ? true : undefined}
          className={`${controlClasses} ${error ? 'border-(--color-border-danger)' : ''}`}
        />
      )}
    </FieldShell>
  );
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function SelectField({ label, hint, error, children, ...rest }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {(id) => (
        <select {...rest} id={id} className={controlClasses}>
          {children}
        </select>
      )}
    </FieldShell>
  );
}
